import React from 'react';
import { Link } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Breadcrumbs, type Crumb } from '../Breadcrumbs';
type ContainerProps = {
  children: React.ReactNode;
  className?: string;
};
export function Container({
  children,
  className = ''
}: ContainerProps) {
  return <div className={`mx-auto w-full max-w-[1240px] px-6 md:px-10 ${className}`} data-id="element-95" data-mp="1lsnfea8yfth1">
      {children}
    </div>;
}
type SectionProps = {
  children: React.ReactNode;
  className?: string;
  id?: string;
  ariaLabel?: string;
};
export function Section({
  children,
  className = '',
  id,
  ariaLabel
}: SectionProps) {
  return <section id={id} aria-label={ariaLabel} className={`py-20 md:py-28 lg:py-36 ${className}`} data-id="element-96" data-mp="1zk0unbzty569">
      <Container data-id="element-97" data-mp="24k6ew73rspeb">{children}</Container>
    </section>;
}

/** Shared section-heading scale so every page steps down identically. */
export const sectionHeadingClass = 'font-display text-[30px] font-medium leading-[1.06] tracking-tightest text-ink sm:text-[36px] md:text-[44px]';
export function Eyebrow({
  children
}: {
  children: React.ReactNode;
}) {
  return <p className="text-[12px] uppercase tracking-[0.18em] text-mist" data-id="element-98" data-mp="30kg9cntfr88w">{children}</p>;
}
type ButtonLinkProps = {
  to: string;
  children: React.ReactNode;
  variant?: 'solid' | 'outline';
};
export function ButtonLink({
  to,
  children,
  variant = 'solid'
}: ButtonLinkProps) {
  const base = 'inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-[14.5px] font-medium transition-colors';
  const styles = variant === 'solid' ? 'bg-ink text-paper hover:bg-navy' : 'border border-line bg-transparent text-ink hover:border-ink';
  return <Link to={to} className={`${base} ${styles}`} data-id="element-99" data-mp="3al0g25zq54yi">
      {children}
      <ArrowForwardIcon sx={{
      fontSize: 17
    }} data-id="element-100" data-mp="awpc7s34rr5s" />
    </Link>;
}
type PageHeroProps = {
  eyebrow: string;
  title: string;
  subtitle: string;
  crumbs?: Crumb[];
};
export function PageHero({
  eyebrow,
  title,
  subtitle,
  crumbs
}: PageHeroProps) {
  return <section className="border-b border-line" data-id="element-101" data-mp="20f5v7o51ua0n">
      <Container className="pb-14 pt-10 md:pb-24 md:pt-16" data-id="element-102" data-mp="25cxskxogqoc8">
        {crumbs && <Breadcrumbs items={crumbs} data-id="element-103" data-mp="25f0izitj2jhh" />}
        <Eyebrow data-id="element-104" data-mp="1qokwm0pdl5hq">{eyebrow}</Eyebrow>
        <div className="mt-7 grid gap-6 md:mt-8 md:grid-cols-12 md:items-end md:gap-8" data-id="element-105" data-mp="1qn9tm33r81ro">
          <h1 className="font-display text-[38px] font-medium leading-[1.03] tracking-tightest text-ink sm:text-[46px] md:col-span-7 md:text-[62px] lg:text-[74px]" data-id="element-106" data-mp="1vm2xr4x5igf6">
            {title}
          </h1>
          <p className="max-w-prose text-[16.5px] leading-relaxed text-graphite md:col-span-5 md:pb-3 md:text-[17px]" data-id="element-107" data-mp="1mnhk9o6jdrl9">
            {subtitle}
          </p>
        </div>
      </Container>
    </section>;
}
export function ClosingCTA({
  headline = 'Interested in NexLaw?',
  body = 'We are building a modern legal experience for Ontario and Quebec. We would be glad to tell you more.'
}: {
  headline?: string;
  body?: string;
}) {
  return <section className="bg-ink text-paper" data-id="element-108" data-mp="36zx9xasul3pm">
      <Container className="py-20 md:py-28 lg:py-32" data-id="element-109" data-mp="ve64nkjjo0eo">
        <div className="grid gap-8 md:grid-cols-12 md:items-end md:gap-10" data-id="element-110" data-mp="vclb9y8yunie">
          <h2 className="font-display text-[34px] font-medium leading-[1.05] tracking-tightest sm:text-[40px] md:col-span-7 md:text-[54px]" data-id="element-111" data-mp="qho7b5qjn4cd">
            {headline}
          </h2>
          <div className="md:col-span-5" data-id="element-112" data-mp="10d92ol13x6d5">
            <p className="max-w-prose text-[16px] leading-relaxed text-paper/70" data-id="element-113" data-mp="10c7pa9wmc7s9">
              {body}
            </p>
            <div className="mt-8 flex flex-wrap gap-3" data-id="element-114" data-mp="1a43z2i82hmcb">
              <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-paper px-6 py-3 text-[14.5px] font-medium text-ink transition-colors hover:bg-white focus-visible:outline-paper" data-id="element-115" data-mp="1a3kjv99sxc1z">
                Contact us
                <ArrowForwardIcon sx={{
                fontSize: 17
              }} data-id="element-116" data-mp="3pvrl3iljlugc" />
              </Link>
              <Link to="/how-it-works" className="inline-flex items-center gap-2 rounded-full border border-paper/25 px-6 py-3 text-[14.5px] font-medium text-paper transition-colors hover:border-paper/60 focus-visible:outline-paper" data-id="element-117" data-mp="3kworcub0jano">
                Learn more
              </Link>
            </div>
          </div>
        </div>
      </Container>
    </section>;
}